import { Checkbox, MenuItem, Stack, Typography, useTheme } from '@mui/joy';
import { Trash } from '@phosphor-icons/react';
import { GridApi } from 'ag-grid-community';
import { MouseEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'core/components';
import { LabeledValue } from 'core/elements';
import { LocalStorageKeys } from 'core/enums';
import { useTableActions } from 'core/hooks';
import { AgGridDataRow, ICustomFilter } from 'core/types';
import { formatDate, jsonToObject, localStorage, trimTextFilterModel } from 'core/utils';
import { Color, Direction, Level, Variant } from 'theme/enums';

interface IProps {
  gridApi: GridApi<AgGridDataRow>;
  filters: ICustomFilter[];
  selectedFilter: ICustomFilter;
  setSelectedFilter: (filter: ICustomFilter) => void;
  currentConfig: string;
  entity: string;
}

export const ManageFilters = ({
  gridApi,
  filters,
  selectedFilter,
  setSelectedFilter,
  currentConfig,
  entity,
}: IProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const { removeCustomFilter, upsertCustomFilter } = useTableActions();

  const removeFilterState = useCallback(() => {
    localStorage.remove(`${LocalStorageKeys.MANAGE_FILTERS_PREFIX}_${entity}`);
  }, [entity]);

  const onDeleteFilter = (e: MouseEvent, id: number) => {
    e.stopPropagation();
    removeCustomFilter({ ids: [id] });
  };

  const onSetDefaultFilter = (e: MouseEvent, id: number, defaultVal: boolean) => {
    e.stopPropagation();
    upsertCustomFilter({ id, default: defaultVal ? 1 : 0 });
  };

  const onSaveCurrentFilterConfig = (e: MouseEvent, id: number) => {
    e.stopPropagation();
    removeFilterState();
    upsertCustomFilter({ id, config: currentConfig });
  };

  const onApplyFilter = (_, filter: ICustomFilter) => {
    setSelectedFilter(filter);

    const config = jsonToObject(filter.config);

    gridApi.setFilterModel(trimTextFilterModel(config.filterModel));
    gridApi.applyColumnState({
      state: config.sortModel,
      defaultState: { sort: null },
    });

    removeFilterState();
  };

  return (
    <Stack sx={{ width: '100%' }}>
      {!filters?.length && (
        <MenuItem disabled>
          <Typography
            level={Level.BODY_MD}
            color={Color.NEUTRAL}
            sx={{ textAlign: 'center', paddingY: 2 }}
          >
            {t('labelNoFiltersSavedForThisTable')}
          </Typography>
        </MenuItem>
      )}
      {filters?.map((filter, index) => (
        <MenuItem
          key={filter.id}
          onClick={(e) => onApplyFilter(e, filter)}
          sx={{
            display: 'block',
            padding: 2,
            backgroundColor:
              filter.id === selectedFilter?.id ? theme.palette.primary[100] : 'inherit',
            borderBottom:
              index === filters.length - 1 ? 'none' : `1px solid ${theme.palette.neutral[200]}`,
            '&.MuiMenuItem-root': { margin: 0 },
          }}
        >
          <Typography level={Level.TITLE_MD}>{filter.name}</Typography>
          <LabeledValue
            label={t('labelLastUpdated')}
            value={formatDate(filter.updatedAt)}
            options={{ inline: true }}
          />
          <Stack direction={Direction.ROW} sx={{ justifyContent: 'space-between', marginTop: 1 }}>
            <Stack direction={Direction.ROW} alignItems="center">
              <Checkbox
                checked={!!filter.default}
                onClick={(e) => onSetDefaultFilter(e, filter.id, !filter.default)}
                label={<Typography color={Color.NEUTRAL}>{t('labelDefaultFilter')}</Typography>}
              />
            </Stack>
            <Button
              color={Color.DANGER}
              variant={Variant.PLAIN}
              text={t('buttonLabelRemoveSet')}
              startDecorator={<Trash />}
              onClick={(e) => onDeleteFilter(e, filter.id)}
            />
          </Stack>
          {filter.id === selectedFilter?.id && currentConfig !== JSON.parse(filter.config) && (
            <Stack direction={Direction.ROW} sx={{ alignItems: 'center', marginTop: 1 }}>
              <Button
                variant={Variant.SOLID}
                text={t('buttonLabelSaveChanges')}
                onClick={(e) => onSaveCurrentFilterConfig(e, filter.id)}
              />
              <Button
                variant={Variant.OUTLINED}
                text={t('buttonLabelResetChanges')}
                onClick={(e) => onApplyFilter(e, filter)}
              />
            </Stack>
          )}
        </MenuItem>
      ))}
    </Stack>
  );
};
